import React, { useState, useEffect, useCallback } from 'react';
import { 
  Table, Button, Space, Tag, Modal, message, 
  Input, DatePicker, Select, Form, Upload 
} from 'antd';
import { 
  EditOutlined, DeleteOutlined, EyeOutlined,
  UploadOutlined, PlusOutlined 
} from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';
import apiService from '../services/apiService';

const { RangePicker } = DatePicker;
const { Search } = Input;
const { TextArea } = Input;

const Events = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create' ou 'edit'
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [form] = Form.useForm();
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });
  const [filters, setFilters] = useState({
    status: undefined,
    search: '',
    dateRange: []
  });

  const fetchEvents = useCallback(async (params = {}) => {
    setLoading(true);
    try {
      const { current = pagination.current, pageSize = pagination.pageSize } = params;
      const { status, search, dateRange } = filters;
      
      const eventParams = {
        page: current,
        limit: pageSize,
        status,
        search
      };
      
      if (dateRange?.length === 2) {
        eventParams.startDate = dateRange[0].toISOString();
        eventParams.endDate = dateRange[1].toISOString();
      }

      const response = await axios.get(apiService.admin.getEvents(eventParams), {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      setEvents(response.data.events);
      setPagination({
        current: response.data.currentPage,
        pageSize: pageSize,
        total: response.data.total
      });
    } catch (error) {
      console.error('Erreur:', error);
      message.error('Erreur lors du chargement des événements');
    }
    setLoading(false);
  }, [filters]);

  useEffect(() => {
    fetchEvents({ current: 1, pageSize: 10 });
  }, [filters]);

  const handleTableChange = (newPagination, filters, sorter) => {
    fetchEvents({
      current: newPagination.current,
      pageSize: newPagination.pageSize,
      sortField: sorter.field,
      sortOrder: sorter.order,
      ...filters,
    });
  };

  const handleStatusChange = async (eventId, newStatus) => {
    try {
      await axios.patch(
        apiService.admin.updateEventStatus(eventId),
        { status: newStatus },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      message.success('Statut mis à jour avec succès');
      fetchEvents(pagination);
    } catch (error) {
      console.error('Erreur:', error);
      message.error('Erreur lors de la mise à jour du statut');
    }
  };

  const handleDelete = async (eventId) => {
    try {
      await axios.delete(
        apiService.admin.deleteEvent(eventId),
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      message.success('Événement supprimé avec succès');
      fetchEvents(pagination);
    } catch (error) {
      console.error('Erreur:', error);
      message.error('Erreur lors de la suppression');
    }
  };

  const showModal = (mode, event = null) => {
    setModalMode(mode);
    setSelectedEvent(event);
    if (event) {
      form.setFieldsValue({
        ...event,
        startDate: dayjs(event.startDate),
        endDate: dayjs(event.endDate),
      });
    } else {
      form.resetFields();
    }
    setIsModalVisible(true);
  };

  const handleModalSubmit = async (values) => {
    try {
      const formData = new FormData();
      Object.keys(values).forEach(key => {
        if (key === 'logo' && values[key]?.fileList?.[0]) {
          formData.append('logo', values[key].fileList[0].originFileObj);
        } else if (key !== 'logo') {
          formData.append(key, values[key]);
        }
      });

      if (modalMode === 'create') {
        await axios.post(
          apiService.admin.createEvent(),
          formData,
          { 
            headers: { 
              Authorization: `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'multipart/form-data'
            } 
          }
        );
        message.success('Événement créé avec succès');
      } else {
        await axios.put(
          apiService.admin.updateEvent(selectedEvent._id),
          formData,
          { 
            headers: { 
              Authorization: `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'multipart/form-data'
            } 
          }
        );
        message.success('Événement mis à jour avec succès');
      }

      setIsModalVisible(false);
      fetchEvents(pagination);
    } catch (error) {
      console.error('Erreur:', error);
      message.error('Erreur lors de l\'enregistrement');
    }
  };

  const columns = [
    {
      title: 'Logo',
      dataIndex: 'logo',
      key: 'logo',
      render: (logo) => logo ? (
        <img 
          src={logo} 
          alt="Logo" 
          className="w-12 h-12 rounded-lg object-cover border border-neutral-200 shadow-sm" 
        />
      ) : null,
    },
    {
      title: 'Titre',
      dataIndex: 'title',
      key: 'title',
      sorter: true,
      render: (title) => (
        <span className="font-medium text-neutral-900 hover:text-primary-600 cursor-pointer">
          {title}
        </span>
      ),
    },
    {
      title: 'Date de début',
      dataIndex: 'startDate',
      key: 'startDate',
      render: (date) => (
        <div className="flex flex-col">
          <span className="text-neutral-900">{new Date(date).toLocaleDateString()}</span>
          <span className="text-xs text-neutral-500">
            {new Date(date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </span>
        </div>
      ),
      sorter: true,
    },
    {
      title: 'Date de fin',
      dataIndex: 'endDate',
      key: 'endDate',
      render: (date) => (
        <div className="flex flex-col">
          <span className="text-neutral-900">{new Date(date).toLocaleDateString()}</span>
          <span className="text-xs text-neutral-500">
            {new Date(date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </span>
        </div>
      ),
      sorter: true,
    },
    {
      title: 'Statut',
      dataIndex: 'status',
      key: 'status',
      render: (status, record) => (
        <Select
          value={status}
          onChange={(value) => handleStatusChange(record._id, value)}
          className="w-32"
          dropdownClassName="shadow-elevation-2 rounded-lg"
        >
          <Select.Option value="En attente">
            <Tag color="warning" className="rounded-full px-3 py-0.5 border-0 bg-warning-50 text-warning-700">
              En attente
            </Tag>
          </Select.Option>
          <Select.Option value="Publié">
            <Tag color="success" className="rounded-full px-3 py-0.5 border-0 bg-success-50 text-success-700">
              Publié
            </Tag>
          </Select.Option>
          <Select.Option value="Annulé">
            <Tag color="error" className="rounded-full px-3 py-0.5 border-0 bg-error-50 text-error-700">
              Annulé
            </Tag>
          </Select.Option>
          <Select.Option value="Terminé">
            <Tag color="default" className="rounded-full px-3 py-0.5 border-0 bg-neutral-100 text-neutral-700">
              Terminé
            </Tag>
          </Select.Option>
        </Select>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button 
            icon={<EyeOutlined />}
            onClick={() => showModal('view', record)}
            className="hover:text-primary-600 hover:border-primary-600 transition-colors"
          />
          <Button 
            icon={<EditOutlined />}
            onClick={() => showModal('edit', record)}
            className="hover:text-primary-600 hover:border-primary-600 transition-colors"
          />
          <Button 
            icon={<DeleteOutlined />}
            danger
            onClick={() => {
              Modal.confirm({
                title: 'Êtes-vous sûr de vouloir supprimer cet événement ?',
                content: 'Cette action est irréversible.',
                okText: 'Oui',
                cancelText: 'Non',
                onOk: () => handleDelete(record._id),
                className: 'shadow-elevation-2'
              });
            }}
            className="hover:bg-error-50"
          />
        </Space>
      ),
    },
  ];

  return (
    <div className="space-y-6 p-6 bg-gradient-to-b from-neutral-50 to-white min-h-screen">
      <div className="flex justify-between items-center">
        <div className="space-y-1">
          <h1 className="text-heading-2 font-heading font-semibold text-neutral-900">
            Gestion des Événements
          </h1>
          <p className="text-neutral-500 text-body-lg">
            Gérez et organisez vos événements en toute simplicité
          </p>
        </div>
        <Button 
          type="primary" 
          icon={<PlusOutlined />}
          onClick={() => showModal('create')}
          className="bg-primary-600 hover:bg-primary-700 shadow-elevation-1 hover:shadow-elevation-2 transition-all px-6 h-11 flex items-center gap-2"
        >
          Créer un événement
        </Button>
      </div>

      <div className="bg-white rounded-xl shadow-elevation-1 overflow-hidden border border-neutral-200">
        <div className="p-6 border-b border-neutral-200">
          <div className="flex gap-4 flex-wrap">
            <Search
              placeholder="Rechercher un événement"
              onSearch={(value) => setFilters({ ...filters, search: value })}
              className="w-72 shadow-sm"
              allowClear
            />
            <Select
              placeholder="Filtrer par statut"
              className="w-48 shadow-sm"
              allowClear
              onChange={(value) => setFilters({ ...filters, status: value })}
            >
              <Select.Option value="En attente">
                <span className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-warning-500" />
                  En attente
                </span>
              </Select.Option>
              <Select.Option value="Publié">
                <span className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-success-500" />
                  Publié
                </span>
              </Select.Option>
              <Select.Option value="Annulé">
                <span className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-error-500" />
                  Annulé
                </span>
              </Select.Option>
              <Select.Option value="Terminé">
                <span className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-neutral-100" />
                  Terminé
                </span>
              </Select.Option>
            </Select>
            <RangePicker
              onChange={(dates) => setFilters({ ...filters, dateRange: dates })}
              className="shadow-sm"
            />
          </div>
        </div>

        <Table
          columns={columns}
          dataSource={events}
          rowKey="_id"
          pagination={{
            ...pagination,
            className: "px-6 py-4 bg-neutral-50 border-t border-neutral-200",
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} événements`,
          }}
          loading={loading}
          onChange={handleTableChange}
          className="ant-table-custom"
          rowClassName="hover:bg-neutral-50 transition-colors"
        />
      </div>

      <Modal
        title={
          <div className="space-y-1 pb-3 border-b border-neutral-200">
            <h3 className="text-heading-4 font-heading font-semibold text-neutral-900">
              {modalMode === 'create' ? 'Créer un événement' : 'Modifier l\'événement'}
            </h3>
            <p className="text-neutral-500 text-body-sm">
              {modalMode === 'create' 
                ? 'Remplissez les informations pour créer un nouvel événement' 
                : 'Modifiez les informations de l\'événement'}
            </p>
          </div>
        }
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        width={800}
        className="shadow-elevation-3"
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleModalSubmit}
          initialValues={{ status: 'En attente' }}
          className="space-y-6 pt-6"
        >
          <Form.Item
            name="title"
            label="Titre"
            rules={[{ required: true, message: 'Le titre est requis' }]}
          >
            <Input className="rounded-lg shadow-sm" />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
            rules={[{ required: true, message: 'La description est requise' }]}
          >
            <TextArea rows={4} className="rounded-lg shadow-sm" />
          </Form.Item>

          <Form.Item
            name="location"
            label="Lieu"
            rules={[{ required: true, message: 'Le lieu est requis' }]}
          >
            <Input className="rounded-lg shadow-sm" />
          </Form.Item>

          <Space className="w-full justify-between">
            <Form.Item
              name="startDate"
              label="Date de début"
              rules={[{ required: true, message: 'La date de début est requise' }]}
            >
              <DatePicker showTime className="rounded-lg shadow-sm" />
            </Form.Item>

            <Form.Item
              name="endDate"
              label="Date de fin"
              rules={[{ required: true, message: 'La date de fin est requise' }]}
            >
              <DatePicker showTime className="rounded-lg shadow-sm" />
            </Form.Item>
          </Space>

          <Form.Item
            name="logo"
            label="Logo"
          >
            <Upload 
              maxCount={1}
              beforeUpload={() => false}
              listType="picture"
            >
              <Button icon={<UploadOutlined />} className="rounded-lg shadow-sm">Télécharger un logo</Button>
            </Upload>
          </Form.Item>

          <Form.Item
            name="status"
            label="Statut"
          >
            <Select>
              <Select.Option value="En attente">En attente</Select.Option>
              <Select.Option value="Publié">Publié</Select.Option>
              <Select.Option value="Annulé">Annulé</Select.Option>
              <Select.Option value="Terminé">Terminé</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item className="mb-0 pt-4 border-t border-neutral-200">
            <div className="flex justify-end gap-3">
              <Button 
                onClick={() => setIsModalVisible(false)}
                className="hover:bg-neutral-50 transition-colors px-6"
              >
                Annuler
              </Button>
              <Button 
                type="primary" 
                htmlType="submit"
                className="bg-primary-600 hover:bg-primary-700 shadow-sm hover:shadow-md transition-all px-6"
              >
                {modalMode === 'create' ? 'Créer l\'événement' : 'Enregistrer les modifications'}
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Events; 